import React, { useState, useEffect, useContext } from 'react';
import PrintsComponent from './PrintsComponent';
import { EXPORT_DOCUMENTS, EXPORT_DOCUMENT, START_DOCUMENTS_SIGNING, RESET_DOCUMENT_SIGNING, RETRIEVE_PRINTS } from '../mutations';
import {
  DOCUMENT_EXPORTED,
  DOCUMENT_EXPORT_FAILED,
  PRINTS_RETRIEVED,
  PRINTS_UPDATED,
  PRINT_UPDATED,
  REQUEST_SIGNATURE_COMPLETED,
  DOCUMENTS_SIGNED
} from '../subscriptions';
import PropTypes from 'prop-types';
import { docTypes } from '../../constants';
import { useMutation, useConditionalSubscription } from 'features/common/security/hooks';
import { useTranslation } from 'react-i18next';
import { useToast, useSimpleError, useServerErrorHandling, useAuthorization } from 'hooks';
import saveAs from 'file-saver';
import { convertBase64ToFile } from 'utils/base64FileConversion';
import { useFileDownload } from 'hooks/fileHandling';
import FileHistoryContainer from '../../history/components/FileHistoryContainer';
import InsuranceDataContext from 'features/insuranceDocument/stepper/insuranceDataContext';
import AuthorizationCodes from 'features/common/constants/authorizationCodes';

const PrintsContainer = ({ documentId }) => {
  const { documentTypeId, documentStateId } = useContext(InsuranceDataContext);
  const hasRightToViewExportDocuments = useAuthorization(AuthorizationCodes.hasRightToViewExportDocuments, documentTypeId, documentStateId);
  const [prints, setPrints] = useState([]);
  const showError = useSimpleError();
  const addToast = useToast();
  const [loadingPrints, setLoadingPrints] = useState(true);
  const [loadingExport, setLoadingExport] = useState(false);
  const [signingInProgress, setSigningInProgress] = useState(false);
  const [signingDisabled, setSigningDisabled] = useState(true);
  const [printDisabled, setPrintDisabled] = useState(false);
  const [showHistory, setShowHistory] = useState(false);
  const [selectedPrint, setSelectedPrint] = useState({});

  const { t } = useTranslation();

  const [retrievePrints] = useMutation(RETRIEVE_PRINTS, {
    onError: error => {
      showError(error);
    }
  });

  useEffect(() => {
    retrievePrints({
      variables: {
        input: {
          documentId: documentId
        }
      }
    });
  }, [retrievePrints, documentId]);

  useEffect(() => {
    if (prints.some(x => x.selected)) {
      setSigningDisabled(false);
      setPrintDisabled(false);
      return;
    }
    setSigningDisabled(true);
    setPrintDisabled(true);
  }, [prints]);

  const toggleHistory = show => {
    if (show) {
      setShowHistory(true);
    } else {
      setShowHistory(false);
    }
  };

  const handleOpenHistoryModal = documentPrint => {
    setSelectedPrint(documentPrint);
    setShowHistory(true);
  };

  /* Export */

  const [printFile] = useMutation(EXPORT_DOCUMENT, {
    onError: error => {
      showError(error);
    }
  });

  const handlePrintDocument = async documentPrintId => {
    printFile({
      variables: {
        input: {
          documentPrintId
        }
      }
    });
  };

  const [printFiles] = useMutation(EXPORT_DOCUMENTS, {
    onError: error => {
      setLoadingExport(false);
      setSigningDisabled(false);
      showError(error);
    }
  });

  const handlePrintDocuments = () => {
    setLoadingExport(true);
    const templates = prints.filter(x => x.selected).map(x => x.templateId);
    printFiles({
      variables: {
        input: {
          documentId,
          templates
        }
      }
    });
  };

  /* End Export */

  /* Sign */

  // Selection
  const handleSelectPrint = documentPrintId => {
    setPrints(prev =>
      prev.map(x => {
        if (x.documentPrintId === documentPrintId)
          return {
            ...x,
            selected: !x.selected
          };
        return x;
      })
    );
  };

  // Signature type
  const handleSignatureTypeChange = (documentPrintId, signatureTypeId) => {
    setPrints(prev =>
      prev.map(x => {
        if (x.documentPrintId === documentPrintId)
          return {
            ...x,
            signatureTypeId
          };
        return x;
      })
    );
  };

  // Mutation

  const [startDocumentsSigning] = useMutation(START_DOCUMENTS_SIGNING, {
    onError: error => {
      setSigningInProgress(false);
      setSigningDisabled(false);
      showError(error);
    }
  });

  const handleSignDocuments = () => {
    const selectedPrints = prints.filter(x => x.selected);

    handleDisplaySigningAlerts(selectedPrints);

    const files = prints
      .filter(x => x.selected && x.needsSigning && x.signatureTypeId)
      .map(x => ({
        fileId: x.fileUniqueId,
        documentPrintId: x.documentPrintId,
        templateId: x.templateId,
        signatureTypeId: x.signatureTypeId
      }));

    if (files.length > 0) {
      setSigningInProgress(true);
      startDocumentsSigning({
        variables: {
          input: {
            documentId,
            files
          }
        }
      });
    }
  };

  // Mutation
  const { storeKey: resetDocumentSigningErrorHandling } = useServerErrorHandling({
    effect: _ => {
      setSigningInProgress(false);
      setSigningDisabled(false);
    }
  });

  const [resetDocumentSigning] = useMutation(RESET_DOCUMENT_SIGNING, {
    onError: error => {
      showError(error);
    },
    onCompleted: data => {
      const { correlationId } = data.resetDocumentSigning;
      resetDocumentSigningErrorHandling({ correlationId });
    }
  });

  const handleResetDocumentSigning = documentPrintId => {
    try {
      resetDocumentSigning({
        variables: {
          input: {
            documentPrintId
          }
        }
      });
    } catch (error) {
      // continue regardless of error
    }
  };

  const handleDisplaySigningAlerts = selectedPrints => {
    var templateNames = '';
    const signedPrints = selectedPrints.filter(x => x.signatureDate);
    if (signedPrints.length > 0) {
      for (const item in signedPrints) {
        templateNames = templateNames.concat(signedPrints[item].templateName).concat(', ');
      }
      if (templateNames !== '') {
        templateNames = templateNames.substring(0, templateNames.lastIndexOf(','));
        addToast(
          t(
            templateNames.indexOf(', ') > 0 ? 'Offer.Edit.PrintData.DocumentsAlreadySigned' : 'Offer.Edit.PrintData.DocumentAlreadySigned',
            { templateName: templateNames }
          ),
          'warning',
          { toastId: 'documentAlreadySignedToast' }
        );
        return;
      }
    }

    templateNames = '';
    for (const item in selectedPrints) {
      if (!selectedPrints[item].needsSigning) {
        templateNames = templateNames.concat(selectedPrints[item].templateName).concat(', ');
      }
    }

    if (templateNames !== '') {
      templateNames = templateNames.substring(0, templateNames.lastIndexOf(','));
      addToast(
        t(
          templateNames.indexOf(', ') > 0
            ? 'Offer.Edit.PrintData.DocumentsNotConfiguredForSigning'
            : 'Offer.Edit.PrintData.DocumentNotConfiguredForSigning',
          { templateName: templateNames }
        ),
        'warning',
        { toastId: 'documentNotConfiguredForSigningToast' }
      );
      return;
    }

    const printsWithoutSignatures = selectedPrints.filter(x => x.needsSigning && !x.signatureTypeId);
    if (printsWithoutSignatures.length > 0) {
      templateNames = '';
      printsWithoutSignatures.forEach(x => {
        templateNames = templateNames.concat(x.templateName).concat(', ');
      });
      addToast(
        t('Offer.Edit.PrintData.NoSignatureType', {
          templateName: templateNames.substring(0, templateNames.lastIndexOf(','))
        }),
        'warning',
        { toastId: 'printsWithoutSignatureToast' }
      );
      return;
    }
  };

  /* End Sign */

  /* Download */

  const [downloadFile] = useFileDownload();

  const handleDownloadFile = fileUniqueId => {
    if (fileUniqueId) downloadFile(fileUniqueId);
  };

  /* End Download */

  /* Subscriptions */

  useConditionalSubscription(PRINTS_RETRIEVED, {
    variables: { documentId },
    skip: !documentId,
    onData: ({
      data: {
        data: { documentPrintsRetrieved }
      }
    }) => {
      if (documentPrintsRetrieved?.printData?.prints) {
        const printsWithSelection = documentPrintsRetrieved.printData.prints.map(x => {
          const selected = prints && prints.find(y => y.documentPrintId === x.documentPrintId)?.selected;
          return {
            ...x,
            selected: selected ?? false
          };
        });
        setPrints(printsWithSelection);
      }
      setLoadingPrints(false);
    }
  });

  useConditionalSubscription(PRINTS_UPDATED, {
    variables: { documentId },
    skip: !documentId,
    onData: ({
      data: {
        data: { documentPrintsUpdated }
      }
    }) => {
      if (documentPrintsUpdated?.printData?.prints) {
        const printsWithSelection = documentPrintsUpdated.printData.prints.map(x => {
          const selected = prints && prints.find(y => y.documentPrintId === x.documentPrintId)?.selected;
          return {
            ...x,
            selected: selected ?? false
          };
        });
        setPrints(printsWithSelection);
      }
      setLoadingPrints(false);
    }
  });

  useConditionalSubscription(PRINT_UPDATED, {
    variables: { documentId },
    skip: !documentId,
    onData: ({
      data: {
        data: { documentPrintUpdated }
      }
    }) => {
      setPrints(prevState =>
        prevState.map(print =>
          print.documentPrintId === documentPrintUpdated.print.documentPrintId
            ? { ...documentPrintUpdated.print, actionInProgress: false, withLensUpdate: true }
            : print
        )
      );
      if (!documentPrintUpdated.silentMode)
        addToast(t('Offer.Edit.PrintData.DocumentPrintUpdated'), 'success', { toastId: 'documentPrintUpdatedToast' });
    }
  });

  useConditionalSubscription(DOCUMENT_EXPORTED, {
    onData: ({ data: { data } }) => {
      if (data.documentExported.silentMode) {
        setLoadingExport(false);
        return;
      }
      saveAs(convertBase64ToFile(data.documentExported.exportOutput.fileBytes), data.documentExported.exportOutput.fileName);
      addToast(t('Offer.Edit.PrintData.ExportSucceeded', { templateName: data.documentExported.exportOutput.templateName }), 'success', {
        toastId: 'documentExportedToast'
      });
      setLoadingExport(false);
    }
  });

  useConditionalSubscription(DOCUMENT_EXPORT_FAILED, {
    onData: ({ data: { data } }) => {
      addToast(
        t('Offer.Edit.PrintData.ExportFailed', { templateName: data?.documentExportFailed?.templateName }) +
          ' - ' +
          data?.documentExportFailed?.errorMessage || t('General.UnexpectedError'),
        'error',
        { toastId: 'documentExportFailedToast' }
      );
      setLoadingExport(false);
      setSigningDisabled(false);
      setPrintDisabled(false);
    }
  });

  useConditionalSubscription(REQUEST_SIGNATURE_COMPLETED, {
    onData: ({
      data: {
        data: { requestSignatureCompleted }
      }
    }) => {
      if (requestSignatureCompleted.documentId === documentId) {
        if (requestSignatureCompleted.errorMessage)
          addToast(requestSignatureCompleted.errorMessage, 'error', { toastId: 'requestSignatureErrorToast' });
        else addToast(t('Offer.Edit.PrintData.SigningRequested'), 'success', { toastId: 'requestSignatureCompletedToast' });
        setSigningInProgress(false);
      }
    }
  });

  useConditionalSubscription(DOCUMENTS_SIGNED, {
    onData: ({
      data: {
        data: { documentsSigned }
      }
    }) => {
      if (documentsSigned.documentId === documentId) {
        addToast(t('Offer.Edit.PrintData.DocumentsSigned'), 'success', { toastId: 'documentsSignedToast' });
      }
    }
  });

  /* End Subscriptions */
  return (
    <>
      {hasRightToViewExportDocuments && (
        <PrintsComponent
          loadingPrints={loadingPrints}
          loadingExport={loadingExport}
          prints={prints}
          signingInProgress={signingInProgress}
          signingDisabled={signingDisabled}
          printDisabled={printDisabled}
          onOpenHistoryModal={handleOpenHistoryModal}
          onSignDocuments={handleSignDocuments}
          onResetDocumentSigning={handleResetDocumentSigning}
          onPrintDocument={handlePrintDocument}
          onPrintDocuments={handlePrintDocuments}
          onSelectPrint={handleSelectPrint}
          onSignatureTypeChange={handleSignatureTypeChange}
          onDownloadFile={handleDownloadFile}
        />
      )}
      {showHistory && selectedPrint && (
        <FileHistoryContainer
          fileId={selectedPrint.fileUniqueId}
          docType={docTypes.PRINT}
          onDownloadFile={handleDownloadFile}
          onCloseModal={() => toggleHistory(false)}
        />
      )}
    </>
  );
};
PrintsContainer.propTypes = {
  documentId: PropTypes.number.isRequired
};

export default PrintsContainer;
